import { sortedDeductions } from "../lib/deductions";
import type { QcSummary } from "../api/types";

interface Props {
  qc: QcSummary | null | undefined;
}

// Points above this render in the "bad" (orange-red) tone; at or below, amber.
const BAD_PTS = 8;
// Bars share a fixed scale so cards are comparable at a glance; 15 = the heaviest
// single deduction (resolution floor / fps cap).
const BAR_MAX_PTS = 15;
const MAX_ROWS = 3;

/** Card-foot block: why this recording's quality score is not an A.
 *
 * Up to three deductions, heaviest first, each with the points it cost and a bar on a
 * fixed 0-15 pt scale; the header total sums ALL deductions (listed or not). A scored
 * recording with no deductions gets the green check; no score yet renders nothing.
 */
export default function DeductionFooter({ qc }: Props) {
  if (!qc || qc.score == null) return null;

  const deductions = sortedDeductions(qc.health_notes).filter(
    (d) => d.points == null || d.points >= 0.05,
  );
  if (deductions.length === 0) {
    return (
      <div className="ded-block">
        <span className="ded-clean">No deductions</span>
      </div>
    );
  }

  const total = deductions.reduce((sum, d) => sum + (d.points ?? 0), 0);
  const shown = deductions.slice(0, MAX_ROWS);
  const hidden = deductions.length - shown.length;

  return (
    <div className="ded-block">
      <span className="ded-block-title">
        Deductions
        <span className="ded-total">−{total.toFixed(1)} pts</span>
      </span>
      {shown.map((d) => {
        const pts = d.points;
        const tone = pts != null && pts > BAD_PTS ? "bad" : "warn";
        const width = pts != null ? Math.min(100, (pts / BAR_MAX_PTS) * 100) : 0;
        return (
          <div className="ded-row" key={d.raw} title={d.tip ? `${d.raw} — ${d.tip}` : d.raw}>
            <span className="ded-lbl">{d.label}</span>
            <span className="ded-bar">
              {pts != null && <i className={tone} style={{ width: `${width}%` }} />}
            </span>
            <span className={`ded-pts ${tone}`}>{pts != null ? `−${pts.toFixed(1)}` : "—"}</span>
          </div>
        );
      })}
      {hidden > 0 && <span className="ded-hidden">+{hidden} more on the recording page</span>}
    </div>
  );
}
